import ClearIcon from "@mui/icons-material/Clear";
import EventIcon from "@mui/icons-material/Event";
import IconButton from "@mui/material/IconButton";
import { AdapterDayjs } from "@mui/x-date-pickers-pro/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { MobileDatePicker, MobileDatePickerProps } from "@mui/x-date-pickers/MobileDatePicker";
import { useRef, useCallback, MouseEvent } from "react";

import { dayjs, Dayjs } from "@holibob-packages/dayjs";

import { DateTimePickerRenderInput } from "./DateTimePickerRenderInput";

export const FORMAT = "LL";

export type DateInputProps = Omit<MobileDatePickerProps<Dayjs>, "renderInput"> & {
    /**
     * Set to true to format and parse values in UTC
     */
    utc?: boolean;
};

export function DateInput(props: DateInputProps) {
    const { inputRef, readOnly, value, utc = false, onChange, ...restProps } = props;

    const dateInputRef = useRef<HTMLInputElement | null>(null);

    const onDateInputRef = useCallback(
        (input: HTMLInputElement | null) => {
            dateInputRef.current = input;
        },
        [dateInputRef]
    );

    const onClear = useCallback(
        (event: MouseEvent<HTMLButtonElement>) => {
            event.stopPropagation();
            if (!onChange) return;
            onChange(null, { validationError: null });
        },
        [onChange]
    );

    const dValue = value ? (utc ? dayjs.utc(value) : dayjs(value)) : null;

    return (
        <LocalizationProvider dateAdapter={AdapterDayjs}>
            <MobileDatePicker
                value={dValue}
                format={FORMAT}
                readOnly={readOnly}
                slots={{
                    textField: DateTimePickerRenderInput,
                }}
                slotProps={{
                    actionBar: { actions: ["cancel", "accept"] },
                    textField: () => ({
                        "aria-label": "change date",
                        "data-readonly": readOnly,
                        fullWidth: true,
                        InputProps: {
                            placeholder: "",
                            endAdornment: readOnly ? (
                                <EventIcon color="action" />
                            ) : (
                                <>
                                    {dValue && (
                                        <IconButton onClick={onClear}>
                                            <ClearIcon />
                                        </IconButton>
                                    )}
                                    <IconButton onClick={() => dateInputRef.current?.click()}>
                                        <EventIcon />
                                    </IconButton>
                                </>
                            ),
                        },
                    }),
                    toolbar: {
                        hidden: false,
                    },
                }}
                {...restProps}
                onChange={onChange}
                inputRef={(ref) => {
                    onDateInputRef(ref);

                    if (!inputRef) return;

                    //Pass the inputRef to the Field abstraction
                    if (typeof inputRef === "function") {
                        inputRef(ref);
                    } else if (!inputRef.current) {
                        (inputRef as unknown as { current: unknown }).current = ref;
                    }
                }}
            />
        </LocalizationProvider>
    );
}
